import { isRocketInstance } from '@/config/instanceProfile'

export const roleBadgeVariant: Record<string, 'default' | 'info' | 'warning' | 'success'> = {
  admin: 'default',
  manager: 'info',
  bdr: 'info',
  closer: 'warning',
  cs: 'success',
  financeiro: 'success',
  usuario_agrada: 'warning',
  pos_venda: 'success',
}

/** Valores alinhados ao enum Prisma `Role`. */
export const roleOptions = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MANAGER', label: 'Gestor / Gerente' },
  { value: 'BDR', label: 'BDR' },
  { value: 'CLOSER', label: 'Closer' },
  { value: 'CS', label: 'CS / Pós-venda' },
  { value: 'FINANCEIRO', label: 'Financeiro' },
  { value: 'USUARIO_AGRADA', label: 'Usuário Agrada (PJ)' },
]

export function getRoleOptions() {
  if (!isRocketInstance) return roleOptions
  return roleOptions.filter((option) => option.value !== 'USUARIO_AGRADA' && option.value !== 'CS')
}

export const roleLabels: Record<string, string> = Object.fromEntries(
  roleOptions.map((o) => [o.value.toLowerCase(), o.label]),
)

export function getRoleLabel(role: string): string {
  return roleLabels[role.toLowerCase()] ?? role
}

/** Papel MANAGER (UI: Gestor / Gerente) — único que recebe a flag canViewContractFinance. */
export function isManagerRole(role: string | null | undefined): boolean {
  return String(role ?? '').toLowerCase() === 'manager'
}

/** Só ADMIN (super admin) pode ver/editar o toggle de valores financeiros. */
export function canManageContractFinanceFlag(role: string | null | undefined): boolean {
  return String(role ?? '').toLowerCase() === 'admin'
}

/** Desativar usuário / redefinir senha / reenviar convite — somente ADMIN. */
export function canManageUserLifecycle(role: string | null | undefined): boolean {
  return String(role ?? '').toLowerCase() === 'admin'
}

export function defaultNotifyFlagsForRole(role: string): {
  notifyNewFinanceProject: boolean
  notifyFinanceInbox: boolean
} {
  const r = role.toUpperCase()
  if (r === 'MANAGER') return { notifyNewFinanceProject: true, notifyFinanceInbox: false }
  if (r === 'FINANCEIRO') return { notifyNewFinanceProject: false, notifyFinanceInbox: true }
  if (r === 'ADMIN') return { notifyNewFinanceProject: true, notifyFinanceInbox: true }
  return { notifyNewFinanceProject: false, notifyFinanceInbox: false }
}

export function getInitials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
}

export interface UserItem {
  id: string
  name: string
  email: string
  role: string
  setor?: string | null
  active: boolean
  isActive?: boolean
  notifyNewFinanceProject?: boolean
  notifyFinanceInbox?: boolean
  canViewContractFinance?: boolean
  eventModeActive?: boolean
  eventModeActivatedAt?: string | null
  passwordSetupStatus?: 'complete' | 'pending' | 'expired'
  passwordSetupExpiresAt?: string | null
  createdAt: string
}

export function normalizeUser(u: Record<string, unknown>): UserItem {
  const role = String(u.role || 'bdr').toLowerCase()
  const active = (u.isActive ?? u.active ?? true) as boolean
  return {
    ...(u as unknown as UserItem),
    role,
    setor: u.setor ? String(u.setor).toUpperCase() : null,
    active,
    notifyNewFinanceProject: Boolean(u.notifyNewFinanceProject),
    notifyFinanceInbox: Boolean(u.notifyFinanceInbox),
    canViewContractFinance: Boolean(u.canViewContractFinance),
    eventModeActive: Boolean(u.eventModeActive),
    eventModeActivatedAt: u.eventModeActivatedAt ? String(u.eventModeActivatedAt) : null,
    passwordSetupStatus: (u.passwordSetupStatus as UserItem['passwordSetupStatus']) ?? 'complete',
    passwordSetupExpiresAt: u.passwordSetupExpiresAt ? String(u.passwordSetupExpiresAt) : null,
  }
}
